import Observability from '@launchdarkly/observability';
import type { LDPlugin } from 'launchdarkly-js-sdk-common';

import { TestingCommands } from 'enums/testing';
import { getReleaseIdentifier } from 'utils/deviceInfoUtils';
import { isLoggingEnabled } from 'utils/loggingUtils';
import { getTelemetryEnvName } from 'utils/runtimeUtils';
import { isStorybook } from 'utils/storybookUtils';

/**
 * The service identity reported to LaunchDarkly. Shared with the Datadog RUM service so
 * the same session shows up under one name in both tools; the fallback only matters for
 * builds that never configured Datadog.
 */
const SERVICE_NAME = process.env.REACT_APP_FEATURE_DATADOG_RUM_SERVICE || 'evolve-web';

/**
 * How long, in milliseconds, repeated evaluations of the same flag to the same value are
 * collapsed into a single exposure. `useFeatureFlags()` is read on nearly every render,
 * so without a window the exposure stream is mostly React re-render noise.
 */
const FLAG_EXPOSURE_DEDUPE_WINDOW_MILLIS = 60_000;

/**
 * Whether LaunchDarkly observability should send anything. Rides the same gate as Datadog
 * — deployed environments only, silent for anything served locally unless the operator
 * opted in — and stays off in Storybook and Cypress, whose sessions are not real traffic.
 */
export const isLaunchDarklyObservabilityEnabled = (): boolean =>
  isLoggingEnabled() && !isStorybook() && !window[TestingCommands.CYPRESS];

/**
 * The LaunchDarkly plugins to register on the client, empty when observability is gated
 * off. Returns a fresh plugin instance per call: a plugin is bound to the client that
 * registers it, so it cannot be shared or hoisted to a module constant.
 */
export const getLaunchDarklyPlugins = (): LDPlugin[] => {
  if (!isLaunchDarklyObservabilityEnabled()) {
    return [];
  }

  return [
    new Observability({
      environment: getTelemetryEnvName(),
      flagExposureDedupeWindowMillis: FLAG_EXPOSURE_DEDUPE_WINDOW_MILLIS,
      /*
       * URLs, sizes and timings only. Headers and bodies stay off: every GraphQL request
       * carries an auth header, and its payload carries customer data.
       */
      networkRecording: {
        enabled: true,
        recordHeadersAndBody: false,
      },
      /*
       * Datadog RUM and GTM already own click/page-view analytics, and click recording
       * would capture on-screen text a second time.
       */
      productAnalytics: false,
      serviceName: SERVICE_NAME,
      /*
       * No `traceparent` on outgoing requests. Turning this on would need the GraphQL API
       * to allow the header in its CORS preflight first, otherwise every request fails.
       */
      tracingOrigins: false,
      version: getReleaseIdentifier(),
    }),
  ];
};
